import { ButtonView, createLabeledInputText, isWidget, LabeledFieldView, Plugin, View, WidgetToolbarRepository, } from 'ckeditor5';
import { PlaceholderStore } from './store';
function getClosestPlaceholderWidget(selection) {
    const selectionPosition = selection.getFirstPosition();
    if (!selectionPosition) {
        return null;
    }
    const viewElement = selection.getSelectedElement();
    const isCorrectWidgetType = !!(viewElement === null || viewElement === void 0 ? void 0 : viewElement.getCustomProperty('placeholder'));
    const match = viewElement && isCorrectWidgetType && isWidget(viewElement);
    return match ? viewElement : null;
}
/**
 * This plugin adds the widget toolbar to the editor view. The toolbar contains the input for editing the placeholder id,
 * and the buttons for removing the placeholder and saving the changes.
 */
export class PlaceholderWidget extends Plugin {
    static get pluginName() {
        return 'PlaceholderWidget';
    }
    static get requires() {
        return [WidgetToolbarRepository];
    }
    init() {
        if (this.editor.config.get('placeholders.readonly')) {
            return;
        }
        this.widgetToolbar = this.createToolbar();
        this.registerWidgetToolbar();
        // Update input value when selection changes
        this.editor.model.document.selection.on('change', () => {
            this.setInputValue();
        });
        // Update input value when toolbar is rendered
        this.widgetToolbar.on('render', () => {
            this.setInputValue();
        });
        // Update input value when placeholder id attribute changes
        this.editor.model.document.on('change:data', () => {
            const { selection } = this.editor.model.document;
            const selectedElement = selection.getSelectedElement();
            if (selectedElement && selectedElement.is('element', 'placeholder')) {
                this.setInputValue();
            }
        });
    }
    afterInit() {
        if (this.editor.config.get('placeholders.readonly')) {
            return;
        }
        const { editor } = this;
        const widgetToolbarRepository = editor.plugins.get(WidgetToolbarRepository);
        widgetToolbarRepository.register('placeholderToolbar', {
            items: ['removePlaceholder', 'editPlaceholder'],
            getRelatedElement: getClosestPlaceholderWidget,
        });
    }
    // Update the value for the input field based on selected placeholder in the editor.
    setInputValue() {
        const { selection } = this.editor.model.document;
        const selectedElement = selection.getSelectedElement();
        const currentId = selectedElement === null || selectedElement === void 0 ? void 0 : selectedElement.getAttribute('id');
        this.textInput.fieldView.set('value', currentId);
    }
    createToolbar() {
        const { editor } = this;
        const { locale } = editor;
        const view = new View(locale);
        this.textInput = new LabeledFieldView(locale, createLabeledInputText);
        this.textInput.set({ label: 'Placeholder ID' });
        const submitButton = new ButtonView(locale);
        submitButton.set({
            label: 'Save',
            withText: true,
            class: 'pleditor-toolbar-fa-icon-button',
        });
        const handleSubmit = () => {
            var _a;
            const newValue = (_a = this.textInput.fieldView.element) === null || _a === void 0 ? void 0 : _a.value;
            const { selection } = this.editor.model.document;
            const selectedElement = selection.getSelectedElement();
            const isPlaceholderSelected = selectedElement && selectedElement.is('element', 'placeholder');
            if (!isPlaceholderSelected || !newValue) {
                return;
            }
            // Update the store before reconversion so the editing downcast can find the new placeholderId
            const store = this.editor.plugins.get(PlaceholderStore);
            const instanceId = selectedElement.getAttribute('instanceId');
            store.updatePlaceholderId(instanceId, newValue);
            this.editor.model.change((writer) => {
                writer.setAttribute('id', newValue, selectedElement);
                // Force reconversion to update the view
                this.editor.editing.reconvertItem(selectedElement);
            });
        };
        submitButton.on('execute', handleSubmit);
        view.setTemplate({
            tag: 'div',
            children: [this.textInput, submitButton],
        });
        return view;
    }
    registerWidgetToolbar() {
        this.editor.ui.componentFactory.add('removePlaceholder', (locale) => {
            const deleteButton = new ButtonView(locale);
            deleteButton.set({
                label: 'Delete',
                withText: true,
                class: 'pleditor-toolbar-fa-icon-button',
            });
            deleteButton.on('execute', () => {
                const { selection } = this.editor.model.document;
                const selectedElement = selection.getSelectedElement();
                if (selectedElement && selectedElement.is('element', 'placeholder')) {
                    this.editor.model.change((writer) => {
                        writer.remove(selectedElement);
                    });
                }
            });
            return deleteButton;
        });
        this.editor.ui.componentFactory.add('editPlaceholder', () => this.widgetToolbar);
    }
}
